import React, { useState } from "react";
import { Plus, Minus, HelpCircle } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Apa itu faktaproduk.com dan bagaimana platform ini membantu saya?",
    a: (
      <>
        <p>
          <span className="font-bold">faktaproduk.com</span> adalah platform audit produk independen berbasis <span className="font-bold">PI (Product Intelligence)</span> pertama di Indonesia yang bertindak sebagai <span className="font-bold">Chief Auditor pribadi Anda</span> saat berbelanja. Fungsi utama kami adalah membantu Anda membedakan antara <span className="font-bold">klaim produk dari tim marketing</span> dengan <span className="font-bold">realita pengalaman pengguna di lapangan</span>.
        </p>
        <p className="mt-2">
          Anda cukup memasukkan merek dan model spesifik dari produk yang ingin Anda beli. PI kami kemudian akan menyisir, menyaring, dan menganalisis ribuan data ulasan publik untuk melahirkan <span className="font-bold">Executive Audit Report</span> yang berisi Verdict (keputusan), skor kualitas, serta analisis risiko yang konkret sebagai panduan keputusan pembelian Anda.
        </p>
      </>
    ),
    testid: "faq-what",
  },
  {
    q: "Bagaimana cara kerja sistem audit PI di platform ini?",
    a: (
      <>
        <p>Algoritma PI kami bekerja melalui <span className="font-bold">tiga tahapan sistematis</span> untuk memastikan hasil laporan yang kredibel:</p>
        <ol className="mt-3 space-y-3 list-decimal pl-5">
          <li>
            <span className="font-bold">Validasi &amp; Konteks Produk.</span> Sistem memverifikasi nama merek, tipe model, dan kategori industri produk secara presisi. Jika data terlalu buram atau ambigu, PI akan menghentikan proses sejenak untuk meminta konfirmasi Anda agar tidak terjadi salah audit.
          </li>
          <li>
            <span className="font-bold">Audit Integritas Sumber Data.</span> PI kami melacak rekam jejak digital produk dari 6 kanal utama — mulai dari video ulasan di YouTube dan TikTok, konten Instagram, testimoni marketplace (Shopee &amp; Tokopedia), hingga diskusi mendalam di forum komunitas. Di tahap ini, PI secara ketat memisahkan konten <span className="font-bold">Paid</span> (iklan, buzzer, komisi affiliate) dari <span className="font-bold">ulasan organik</span> pembeli.
          </li>
          <li>
            <span className="font-bold">Verdict Berbasis Bukti.</span> Menggunakan metode hierarki bukti, PI memberikan bobot nilai tertinggi pada ulasan pembeli terverifikasi (<em>verified buyers</em>) dan diskusi forum. Hasil akhir dirangkum dalam bentuk status <span className="font-bold">Verdict</span> serta <span className="font-bold">Overall Score (0–100)</span> berdasarkan data 12 bulan terakhir khusus pasar Indonesia.
          </li>
        </ol>
      </>
    ),
    testid: "faq-method",
  },
  {
    q: "Apa arti dari status Verdict (PROCEED, CAUTION, AVOID, dan INSUFFICIENT DATA)?",
    a: (
      <>
        <p>Setiap produk yang selesai diaudit akan mendapatkan satu label status tegas:</p>
        <div className="mt-4 space-y-3">
          <div className="border-l-4 border-[#002FA7] pl-3">
            <span className="font-bold text-[#002FA7]">PROCEED</span> — Sinyal positif dari konsumen nyata sangat dominan dan mendukung klaim iklan brand. Keluhan yang ditemukan bersifat minor, sehingga produk ini <span className="font-bold">sangat aman untuk Anda beli</span>.
          </div>
          <div className="border-l-4 border-[#FFD700] pl-3">
            <span className="font-bold">CAUTION</span> — Ditemukan <span className="font-bold">ketidaksesuaian (diskrepansi)</span> antara janji marketing dan realita di lapangan. Produk tetap berfungsi baik, namun memiliki kelemahan tertentu (<em>deal breaker</em>) yang wajib Anda pelajari sebelum checkout.
          </div>
          <div className="border-l-4 border-[#FF2400] pl-3">
            <span className="font-bold text-[#FF2400]">AVOID</span> — PI mendeteksi pola keluhan fatal yang terjadi secara <span className="font-bold">masif dan berulang</span> dari pengguna asli. Risiko kerugian tinggi; kami menyarankan Anda mencari alternatif lain.
          </div>
          <div className="border-l-4 border-[#666] pl-3">
            <span className="font-bold text-[#666]">INSUFFICIENT DATA</span> — Volume ulasan organik di internet belum mencapai batas minimum kuota algoritma kami, sehingga PI tidak bisa mengeluarkan kesimpulan dan skor yang akurat demi menjaga objektivitas.
          </div>
        </div>
      </>
    ),
    testid: "faq-verdict",
  },
  {
    q: "Apa perbedaan antara fitur Basic Audit (Gratis) dan Full Audit (Premium)?",
    a: (
      <>
        <p>Kami menyediakan <span className="font-bold">dua model analisis produk</span> yang dapat Anda pilih sesuai tingkat kebutuhan riset Anda:</p>
        <div className="mt-4 overflow-x-auto -mx-2 md:mx-0">
          <table className="w-full border-2 border-black border-collapse text-sm md:text-base" data-testid="faq-tier-table">
            <thead>
              <tr className="bg-[#0a0a0a] text-white">
                <th className="border-2 border-black px-3 py-3 text-left font-mono text-[11px] md:text-xs uppercase tracking-[0.18em] font-bold w-[36%]">Fitur</th>
                <th className="border-2 border-black px-3 py-3 text-left font-mono text-[11px] md:text-xs uppercase tracking-[0.18em] font-bold">Basic Audit (Gratis)</th>
                <th className="border-2 border-black px-3 py-3 text-left font-mono text-[11px] md:text-xs uppercase tracking-[0.18em] font-bold bg-[#FFD700] text-[#0a0a0a]">Full Audit (Premium)</th>
              </tr>
            </thead>
            <tbody className="font-sans">
              {[
                ["Harga", "Rp 0", <span className="font-bold">Rp 19.900 / 24 jam</span>],
                ["Kedalaman analisis", "Standar", <span className="font-bold">Mendalam &amp; kontekstual</span>],
                ["Cakupan ulasan / platform", "~6 hasil", <span className="font-bold">~10 hasil</span>],
                ["Verdict & Overall Score", "✓", "✓"],
                ["Reality Check", "✓", "✓"],
                ["Deal Breaker", "✓", "✓"],
                ["Risk Assessment", "✓", "✓"],
                ["Actionable Advice", "✓", "✓"],
                ["Pricing Analysis", <span className="text-[#aaa]">—</span>, "✓"],
                ["Use Case Fit (cocok / hindari)", <span className="text-[#aaa]">—</span>, "✓"],
                ["Competitor Comparison", <span className="text-[#aaa]">—</span>, "✓"],
                ["Recommended Alternatives", <span className="text-[#aaa]">—</span>, "✓"],
                ["Longevity Score", <span className="text-[#aaa]">—</span>, "✓"],
                ["After-Sales Signal", <span className="text-[#aaa]">—</span>, "✓"],
                ["Prioritas antrian", <span className="text-[#aaa]">—</span>, "✓"],
                ["Rate limit per IP", "Aktif", <span className="font-bold">Bebas</span>],
              ].map(([fitur, basic, full], idx) => (
                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#f7f7f7]"}>
                  <td className="border-2 border-black px-3 py-2.5 font-mono text-[12px] md:text-sm uppercase tracking-[0.06em]">{fitur}</td>
                  <td className="border-2 border-black px-3 py-2.5">{basic}</td>
                  <td className="border-2 border-black px-3 py-2.5 bg-[#fff8d6]">{full}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ),
    testid: "faq-tier",
  },
  {
    q: "Bagaimana cara PI menghitung skor akhir (Overall Score) suatu produk?",
    a: (
      <>
        <p>
          <span className="font-bold">Overall Score</span> dirangkum dalam skala nilai <span className="font-bold">0 hingga 100</span> menggunakan algoritma kecerdasan buatan. Komponen penilaiannya mencakup sentimen murni dari ulasan publik, tingkat konsistensi fungsi barang, kekuatan klaim pabrikan versus fakta pemakaian, serta komparasi ketat dengan produk saingan di kelasnya (khusus laporan Premium).
        </p>
        <div className="mt-3 space-y-2 text-sm">
          <div><span className="font-bold">80 – 100 (Excellent):</span> Status PROCEED mutlak; produk unggulan dengan kelemahan yang sangat minim.</div>
          <div><span className="font-bold">60 – 79 (Good):</span> Produk berkualitas bagus dan sangat layak dibeli dengan beberapa kompromi kecil yang wajar.</div>
          <div><span className="font-bold">40 – 59 (Fair):</span> Performa pas-pasan; ada jarak yang cukup mengganggu antara janji iklan dengan fungsi asli produk.</div>
          <div><span className="font-bold">0 – 39 (Poor):</span> Produk bermasalah; didominasi oleh laporan kerusakan fatal yang berulang. Hindari demi keamanan dana Anda.</div>
        </div>
        <p className="mt-3 text-sm">
          <span className="font-bold">Catatan:</span> Jika hasil akhir adalah <em>INSUFFICIENT DATA</em>, maka nilai skor otomatis tidak akan dihitung (<code className="font-mono">null</code>).
        </p>
      </>
    ),
    testid: "faq-score",
  },
  {
    q: "Apakah hasil skor dan verdict audit produk akan selalu sama?",
    a: (
      <p>
        <span className="font-bold">Tidak.</span> Hasil analisis produk di platform kami bersifat <span className="font-bold">sangat dinamis</span> dan mengikuti perkembangan waktu. Jagat digital terus bergerak; setiap kali muncul gelombang ulasan baru atau pembaruan kualitas produk oleh produsen di platform seperti YouTube, TikTok, Shopee, atau Tokopedia, PI kami akan langsung membaca sinyal tersebut. Oleh karena itu, kami sangat menyarankan Anda melakukan <span className="font-bold">audit berkala</span> demi mendapatkan data paling segar tepat sebelum mengambil keputusan pembelian.
      </p>
    ),
    testid: "faq-dynamic",
  },
  {
    q: "Apakah faktaproduk.com benar-benar objektif dan bisa dipercayai?",
    a: (
      <>
        <p>
          <span className="font-bold">Ya</span>, objektivitas dan penyajian <em>review jujur</em> adalah fondasi utama kami berdiri. Sistem PI kami diprogram secara independen untuk mendeteksi sekaligus membuang pengaruh promosi berbayar, agensi buzzer, maupun tautan komisi terafiliasi. Prioritas kami berikan kepada <span className="font-bold">suara konsumen riil</span> di forum komunitas dan pembeli di e-commerce.
        </p>
        <p className="mt-2">
          Perlu dicatat bahwa laporan kami <span className="font-bold">bukan</span> merupakan bentuk endorsement komersial, saran finansial, maupun sertifikasi otoritas resmi — melainkan sebuah <span className="font-bold">interpretasi sains data publik</span> untuk membantu Anda menjadi konsumen modern yang lebih bijak.
        </p>
      </>
    ),
    testid: "faq-objective",
  },
];

export default function Methodology() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section id="how" className="border-b-2 border-black bg-white">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#666666]">/ 03 — FAQ &amp; Metodologi</div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl mt-3 leading-[0.92]">
              Pertanyaan
              <br />
              yang Sering
              <br />
              <span className="text-[#666666]">Ditanyakan.</span>
            </h2>
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.18em] border-2 border-black px-3 py-1.5 inline-flex items-center gap-2">
            <HelpCircle className="w-3.5 h-3.5" />
            {FAQ_ITEMS.length} pertanyaan
          </div>
        </div>

        <div id="examples" className="mt-10 border-2 border-black divide-y-2 divide-black bg-white" data-testid="faq-accordion">
          {FAQ_ITEMS.map((item, idx) => {
            const open = openIdx === idx;
            return (
              <article key={idx} data-testid={item.testid}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  className={`w-full text-left px-5 md:px-7 py-5 flex items-start justify-between gap-4 transition-colors ${open ? "bg-[#FFD700]" : "bg-white hover:bg-[#fff8d6]"}`}
                  aria-expanded={open}
                  data-testid={`${item.testid}-toggle`}
                >
                  <div className="flex items-start gap-3 md:gap-4 flex-1">
                    <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#666] mt-1.5 min-w-[28px]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display font-black uppercase tracking-tight text-lg md:text-2xl leading-snug">
                      {item.q}
                    </h3>
                  </div>
                  <span className="flex-shrink-0 mt-1">
                    {open ? (
                      <Minus className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} />
                    ) : (
                      <Plus className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} />
                    )}
                  </span>
                </button>
                {open && (
                  <div
                    className="px-5 md:px-7 pb-6 pt-2 font-sans text-base leading-relaxed text-[#222]"
                    data-testid={`${item.testid}-answer`}
                  >
                    <div className="md:pl-[44px]">{item.a}</div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
